import { domToReact, Element, Node, Text } from "html-react-parser";
import { adocLinkDataAttribute } from "../const";
import cn from "classnames";
import React, { ReactElement } from "react";
import { MdCode, MdNetworkWifi, MdSecurity, MdTimeline } from "react-icons/md";
import { IoMdBeaker } from "react-icons/io";

export function isAdocLink(domNode: Element): boolean {
  return (
    domNode.type === "tag" &&
    domNode.name === "a" &&
    adocLinkDataAttribute in domNode.attribs
  );
}

export function returnAnchor(id: string): ReactElement {
  return (
    <div
      className={cn(
        "anchor absolute inset-0 -z-50",
        // positioning shenanigans to compensate for sticky top navbar
        "-top-[90px] bottom-[90px]",
        "lg:-top-[120px] lg:bottom-[120px]",
      )}
      id={id}
    />
  );
}

export function getAdocLink(domNode: Element): string | null {
  if (!isAdocLink(domNode)) {
    return null;
  }
  return domNode.attribs.href;
}

export function isMainSection(domNode: Element): boolean {
  return (
    domNode.type === "tag" &&
    domNode.name === "div" &&
    getClasses(domNode).includes("sect1")
  );
}

export function isSecondSection(domNode: Element): boolean {
  return (
    domNode.type === "tag" &&
    domNode.name === "div" &&
    getClasses(domNode).includes("sect2")
  );
}

export function isThirdSection(domNode: Element): boolean {
  return (
    domNode.type === "tag" &&
    domNode.name === "div" &&
    getClasses(domNode).includes("sect3")
  );
}

export function isMainHeading(domNode: Element): boolean {
  return domNode.type === "tag" && domNode.name === "h2";
}

export function isCopyToClipboardButton(domNode: Element): boolean {
  return (
    domNode.type === "tag" &&
    getClasses(domNode).includes("copy-to-clipboard-button")
  );
}

export function parseTabbedCodeBlockElement(
  domNode: Element,
): TabbedChildListType[] {
  let result: TabbedChildListType[] = [];
  const firstItemLanguage = getLanguageOfListingElement(domNode);
  const nextSibling = domNode.nextSibling;
  const secondNextSibling = domNode.nextSibling?.nextSibling;
  if (isSiblingHasWhiteSpaces(nextSibling)) {
    if (
      secondNextSibling instanceof Element &&
      isListingElement(secondNextSibling) &&
      getLanguageOfListingElement(secondNextSibling) !== firstItemLanguage
    ) {
      const tabs: TabbedChildListType[] = [];
      tabs.push(
        createNewCodeBlockTab(
          domNode,
          extractLanguageFromClassname(firstItemLanguage),
        ),
      );
      addNextSiblingTab(domNode, firstItemLanguage, tabs);
      result = tabs;
    }
  }

  return result;
}

export function parseTabbedBlockElement(domNode: Element) {
  let result: TabbedBlockChildListType[] = [];
  const nextSibling = domNode.nextSibling;
  const secondNextSibling = domNode.nextSibling?.nextSibling;
  if (isSiblingHasWhiteSpaces(nextSibling)) {
    if (
      secondNextSibling instanceof Element &&
      isOpenBlockElement(secondNextSibling)
    ) {
      const tabs: TabbedBlockChildListType[] = [];
      const id = getId(domNode);
      if (id) {
        tabs.push(createNewBlockTab(domNode, id));
        addNextSiblingBlockToTabs(domNode, tabs);
        result = tabs;
      }
    }
  }
  return result;
}

export function addNextSiblingBlockToTabs(
  domNode: Element,
  result: TabbedBlockChildListType[],
) {
  const nextSibling = domNode.nextSibling;
  const secondNextSibling = domNode.nextSibling?.nextSibling;
  if (isSiblingHasWhiteSpaces(nextSibling)) {
    if (
      secondNextSibling instanceof Element &&
      isOpenBlockElement(secondNextSibling)
    ) {
      const id = getId(secondNextSibling);
      if (id) {
        result.push(createNewBlockTab(secondNextSibling, id));
        addNextSiblingBlockToTabs(secondNextSibling, result);
      }
    }
  }
}

export function createNewBlockTab(domNode: Element, id: string) {
  const tabContents = removeTitleFromBlock(domNode.children);
  const title: Node[] = domNode.children.filter(
    (child) =>
      child instanceof Element &&
      child.tagName === "div" &&
      getClasses(child).includes("title"),
  );
  return {
    id: id,
    renderedContent: domToReact(tabContents),
    title: title.length > 0 ? toText(title[0]) : undefined,
  };
}

export function addNextSiblingTab(
  domNode: Element,
  language: string,
  result: TabbedChildListType[],
) {
  const nextSibling = domNode.nextSibling;
  const secondNextSibling = domNode.nextSibling?.nextSibling;

  if (isSiblingHasWhiteSpaces(nextSibling)) {
    if (
      secondNextSibling instanceof Element &&
      isListingElement(secondNextSibling)
    ) {
      const nextSiblingLanguage =
        getLanguageOfListingElement(secondNextSibling);
      if (nextSiblingLanguage !== language) {
        result.push(
          createNewCodeBlockTab(secondNextSibling, nextSiblingLanguage),
        );
        addNextSiblingTab(secondNextSibling, nextSiblingLanguage, result);
      }
    }
  }
}

export function createNewCodeBlockTab(
  domNode: Element,
  language: string,
): TabbedChildListType {
  const tabContents = removeCopyToClipboardFromItems(domNode.children);
  return {
    renderedContent: domToReact(tabContents),
    language: extractLanguageFromClassname(language),
    textContent: tabContents.map(toText).join(""),
  };
}

export function removeTitleFromBlock(children: Node[]): Node[] {
  return children.filter(
    (child) =>
      !(child instanceof Element && getClasses(child).includes("title")),
  );
}

export function removeCopyToClipboardFromItems(children: Node[]): Node[] {
  return children.filter(
    (child) => !(child instanceof Element && isCopyToClipboardButton(child)),
  );
}

export function isListingElement(domNode: Element): boolean {
  return domNode.type === "tag" && getClasses(domNode).includes("listingblock");
}

export function isOpenBlockElement(domNode: Element): boolean {
  const id = getId(domNode);
  if (id) {
    return domNode.type === "tag" && getClasses(domNode).includes("openblock");
  }
  return false;
}

export function isNonHeadingAnchor(domNode: Element): boolean {
  return !!(
    getId(domNode) &&
    !getClasses(domNode).includes("openblock") &&
    domNode.tagName !== "h1" &&
    domNode.tagName !== "h2" &&
    domNode.tagName !== "h3" &&
    domNode.tagName !== "h4"
  );
}

export function isChildOfTabbedCodeBlock(domNode: Element): boolean {
  const prevSibling = domNode.previousSibling;
  const secondPrevSibling = domNode.previousSibling?.previousSibling;
  if (isListingElement(domNode) && isSiblingHasWhiteSpaces(prevSibling)) {
    if (secondPrevSibling && secondPrevSibling instanceof Element) {
      const prevSiblingClassname =
        getLanguageOfListingElement(secondPrevSibling);
      if (
        prevSiblingClassname &&
        prevSiblingClassname.includes("language") &&
        prevSiblingClassname !== getLanguageOfListingElement(domNode)
      ) {
        return true;
      }
    }
  }

  return false;
}

export function hasPreviousListingBlock(domNode: Element) {
  const secondPrevElement = domNode.previousSibling?.previousSibling;
  const prevElement = domNode.previousSibling;

  if (isSiblingHasWhiteSpaces(prevElement)) {
    if (
      secondPrevElement instanceof Element &&
      getClasses(secondPrevElement).includes("listingblock") &&
      getLanguageOfListingElement(domNode) !==
        getLanguageOfListingElement(secondPrevElement)
    ) {
      return true;
    }
  }
  return false;
}

export function hasPreviousOpenBlock(domNode: Element) {
  const secondPrevElement = domNode.previousSibling?.previousSibling;
  const prevElement = domNode.previousSibling;
  if (isSiblingHasWhiteSpaces(prevElement)) {
    if (
      secondPrevElement instanceof Element &&
      isOpenBlockElement(secondPrevElement)
    ) {
      return true;
    }
  }
  return false;
}

export function getHeadingForMainSection(domNode: Element) {
  const heading = domNode.children.find(
    (c) => c instanceof Element && isMainHeading(c),
  );
  return heading as Element | undefined;
}

export function getHeadingForSecondSection(domNode: Element) {
  const heading = domNode.children.find(
    (c) => c instanceof Element && isFirstLevelSubHeading(c),
  );
  return heading as Element | undefined;
}

export function isFirstLevelSubHeading(domNode: Element): boolean {
  return domNode.type === "tag" && domNode.name === "h3";
}

export function getHeadingForThirdSection(domNode: Element) {
  const heading = domNode.children.find(
    (c) => c instanceof Element && isSecondLevelSubHeading(c),
  );
  return heading as Element | undefined;
}

export function getHeadingForSecondSubHeading(domNode: Element) {
  const heading = domNode.children.find(
    (c) => c instanceof Element && isSecondLevelSubHeading(c),
  );
  return heading as Element | undefined;
}

export function isSecondLevelSubHeading(domNode: Element): boolean {
  return domNode.type === "tag" && domNode.name === "h4";
}

export function hasAnchor(domNode: Element): boolean {
  return !!domNode.attribs["id"];
}

export function getId(domNode: Element): string | undefined {
  return domNode.attribs["id"];
}

export function getClasses(domNode: Element): string[] {
  return domNode.attribs["class"]?.split(" ") ?? [];
}

export function isText(domNode: Node): domNode is Text {
  return domNode.type === "text";
}

export function toText(domNode: Node): string {
  if (isText(domNode)) return domNode.data;
  if (domNode instanceof Element) return domNode.children.map(toText).join("");
  return "";
}

export function getLanguageOfListingElement(domeNode: Element): string {
  let preClassName = "";
  domeNode.children.find((node) => {
    if (node instanceof Element && getClasses(node).includes("content")) {
      node.children.forEach((child) => {
        if (
          child instanceof Element &&
          child.type === "tag" &&
          child.name === "pre"
        ) {
          preClassName = child.attribs["class"];
        }
      });
    }
  });
  return preClassName;
}

export function isSiblingHasWhiteSpaces(domNode: Node | null): boolean {
  return !!(domNode && isText(domNode) && hasWhiteSpaces(domNode.data));
}

export function isAdmonitionElement(domNode: Element): boolean {
  return getClasses(domNode).includes("admonitionblock");
}

export function parseAdmonitionNode(domNode: Element): ParsedAdmonitionType {
  let type: "WARNING" | "TIP" | "NOTE" | "IMPORTANT" | "CAUTION" = "IMPORTANT";
  if (getClasses(domNode).includes("warning")) type = "WARNING";
  if (getClasses(domNode).includes("tip")) type = "TIP";
  if (getClasses(domNode).includes("note")) type = "NOTE";
  if (getClasses(domNode).includes("important")) type = "IMPORTANT";
  if (getClasses(domNode).includes("caution")) type = "CAUTION";
  let children: Node[] = [];
  const table = domNode.children.find(
    (child) => child instanceof Element && child.tagName === "table",
  );
  if (table && table instanceof Element) {
    const tBody = table.children.find(
      (child) => child instanceof Element && child.tagName === "tbody",
    );
    if (tBody && tBody instanceof Element) {
      const tr = tBody.children.find(
        (child) => child instanceof Element && child.tagName === "tr",
      );
      if (tr && tr instanceof Element) {
        children = tr.children.filter(
          (child) =>
            child instanceof Element &&
            child.tagName === "td" &&
            getClasses(child).includes("content"),
        );
      }
    }
  }

  return {
    title: extractTitle(children),
    content: removeAdmonitionTitleFromBlock(children),
    type: type,
  };
}

export function extractTitle(children: Node[]): string | undefined {
  const child = children[0];
  if (child && child instanceof Element) {
    const title = child.children.filter(
      (child) =>
        child instanceof Element && getClasses(child).includes("title"),
    )[0];
    if (title && title instanceof Element) {
      return toText(title);
    }
  }
  return undefined;
}

export function removeAdmonitionTitleFromBlock(children: Node[]): Node[] {
  const child = children[0];
  if (child && child instanceof Element) {
    return child.children.filter(
      (child) =>
        !(child instanceof Element && getClasses(child).includes("title")),
    );
  }
  return children.filter(
    (child) =>
      !(child instanceof Element && getClasses(child).includes("title")),
  );
}

export function hasWhiteSpaces(data: string): boolean {
  return /^\s*$/.test(data);
}

export function isNextSiblingCodeBlock(domeNode: Element): boolean {
  const nextSibling = domeNode.nextSibling;
  const secondNextSibling = domeNode.nextSibling?.nextSibling;

  if (isSiblingHasWhiteSpaces(nextSibling)) {
    if (
      secondNextSibling instanceof Element &&
      isListingElement(secondNextSibling)
    ) {
      return true;
    }
  }

  return false;
}

export function getIconForTiles(id: string) {
  switch (id) {
    case "_user_guide":
      return <IoMdBeaker className="mx-1 text-vivid-orange" size={25} />;
    case "_admin_guide":
      return <MdSecurity className="mx-1 text-vivid-orange" size={25} />;
    case "_api_guide":
      return <MdCode className="mx-1 text-vivid-orange" size={25} />;
    case "_release_notes":
      return <MdTimeline className="mx-1 text-vivid-orange" size={25} />;
    default:
      return <MdNetworkWifi />;
  }
}

export function fetchIdIfHasLinkChild(domNode: Element): string | undefined {
  if (getClasses(domNode).includes("paragraph") && domNode.children[1]) {
    const domChild = domNode.children.filter(
      (child) => child.type === "tag",
    )[0];
    if (domChild instanceof Element && domChild.tagName === "p") {
      const link = domChild.children.filter((child) => child.type === "tag")[0];
      if (
        link instanceof Element &&
        link.tagName === "a" &&
        link.attribs["href"]
      ) {
        const id = getId(link);
        if (id) {
          return id;
        }
      }
    }
  }
  return undefined;
}

export function extractSectionbody(nodes: Node[]) {
  return nodes.filter(
    (node) =>
      node instanceof Element && getClasses(node).includes("sectionbody"),
  );
}

export function excludeSectionbody(nodes: Node[]) {
  return nodes.filter(
    (node) =>
      !(node instanceof Element && getClasses(node).includes("sectionbody")),
  );
}

export type ParsedAdmonitionType = {
  title: string | undefined;
  content: Node[];
  type: "WARNING" | "TIP" | "NOTE" | "IMPORTANT" | "CAUTION";
};

export type TabbedChildListType = {
  language: string;
  textContent: string;
  renderedContent: string | JSX.Element | JSX.Element[];
};

export type TabbedBlockChildListType = {
  id: string;
  renderedContent: string | JSX.Element | JSX.Element[];
  title: string | undefined;
};

export function extractLanguageFromClassname(className: string): string {
  let lang = className;
  lang = lang.replace("highlight", "");
  lang = lang.replace("language-", "");
  return lang.toUpperCase();
}
